import { getFormTabErrorCounts } from '../../../../../lib/formTabErrors';
import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type FieldErrors } from 'react-hook-form';
import { AppWindow, Globe2, Settings2 } from 'lucide-react';
import {
  DashboardPageFormActions,
  DashboardPageFormShell,
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
  DashboardPageFormTabs,
} from '@shigo-ui/dashboard-ui';
import { Button, Form, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { dashboardApiErrorMessage } from '../../../../../lib/apiErrorMessages';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { dashboardT } from '../../../../../lib/messages';
import { useTailoredSupplyMutation } from '../../hooks';
import {
  TailoredSupplyEditorTab,
  type TailoredSupplyApp,
  type TailoredSupplyDomain,
} from '../../model';
import { useTailoredSupplyCreateRouteFilters } from '../../useRouteFilters';
import {
  tailoredSupplyCreateFormSchema,
  type TailoredSupplyCreateFormValue,
} from '../../createFormSchema';
import { TailoredSupplyAppsTable } from '../TailoredSupplyAppsTable/TailoredSupplyAppsTable';
import { TailoredSupplyDomainsTable } from '../TailoredSupplyDomainsTable/TailoredSupplyDomainsTable';
import { TailoredSupplyGeneralFields } from './TailoredSupplyGeneralFields';

export function TailoredSupplyCreatePage() {
  const { returnTo } = useReturnNavigation({ to: '/supply/tailored-supply' });
  const routeFilters = useTailoredSupplyCreateRouteFilters();
  const mutation = useTailoredSupplyMutation();
  const form = useForm<TailoredSupplyCreateFormValue>({
    defaultValues: { name: '', description: '' },
    resolver: zodResolver(tailoredSupplyCreateFormSchema),
    mode: 'onSubmit',
  });
  const [selectedApps, setSelectedApps] = React.useState<Map<string, TailoredSupplyApp>>(
    () => new Map(),
  );
  const [selectedDomains, setSelectedDomains] = React.useState<Map<string, TailoredSupplyDomain>>(
    () => new Map(),
  );
  const hasSelection = selectedApps.size > 0 || selectedDomains.size > 0;

  function updateApps(items: TailoredSupplyApp[], selected: boolean) {
    setSelectedApps((current) => updateSelection(current, items, selected));
  }

  function updateDomains(items: TailoredSupplyDomain[], selected: boolean) {
    setSelectedDomains((current) => updateSelection(current, items, selected));
  }

  async function save(value: TailoredSupplyCreateFormValue) {
    form.clearErrors('root.server');
    try {
      await mutation.mutateAsync({
        name: value.name.trim(),
        description: value.description.trim() || null,
        appIds: [...selectedApps.keys()],
        domainIds: [...selectedDomains.keys()],
      });
    } catch (error) {
      form.setError('root.server', { message: dashboardApiErrorMessage(error) });
      return;
    }
    toast.success(dashboardT('tailoredSupply.created'));
    try {
      await returnTo({ replace: true });
    } catch {
      toast.error(dashboardT('tailoredSupply.redirectError'));
    }
  }

  function invalidSubmit(errors: FieldErrors<TailoredSupplyCreateFormValue>) {
    if (errors.name || errors.description) routeFilters.setTab(TailoredSupplyEditorTab.General);
  }

  const tabErrors = getFormTabErrorCounts(
    form.formState.errors,
    {},
    TailoredSupplyEditorTab.General,
  );

  return (
    <DashboardPageFormShell
      title={dashboardT('tailoredSupply.create')}
      backAction={
        <ReturnBackLink
          fallback={{ to: '/supply/tailored-supply' }}
          label={dashboardT('tailoredSupply.back')}
        />
      }
    >
      <Form {...form}>
        <form
          noValidate
          onSubmit={(event) => {
            void form.handleSubmit(save, invalidSubmit)(event);
          }}
        >
          <DashboardPageFormTabs
            value={routeFilters.tab}
            onValueChange={(tab) => {
              routeFilters.setTab(tab as TailoredSupplyEditorTab);
            }}
          >
            <DashboardPageFormTabList>
              <DashboardPageFormTab
                value={TailoredSupplyEditorTab.General}
                errorCount={tabErrors[TailoredSupplyEditorTab.General]}
                errorLabel={(count) => dashboardT('common.validationErrors', { count })}
                icon={<Settings2 className="h-4 w-4" aria-hidden="true" />}
              >
                {dashboardT('tailoredSupply.general')}
              </DashboardPageFormTab>
              <DashboardPageFormTab
                value={TailoredSupplyEditorTab.Apps}
                errorCount={tabErrors[TailoredSupplyEditorTab.Apps]}
                errorLabel={(count) => dashboardT('common.validationErrors', { count })}
                icon={<AppWindow className="h-4 w-4" aria-hidden="true" />}
              >
                {dashboardT('tailoredSupply.apps')}
              </DashboardPageFormTab>
              <DashboardPageFormTab
                value={TailoredSupplyEditorTab.Domains}
                errorCount={tabErrors[TailoredSupplyEditorTab.Domains]}
                errorLabel={(count) => dashboardT('common.validationErrors', { count })}
                icon={<Globe2 className="h-4 w-4" aria-hidden="true" />}
              >
                {dashboardT('tailoredSupply.domains')}
              </DashboardPageFormTab>
            </DashboardPageFormTabList>
            <DashboardPageFormTabPanel value={TailoredSupplyEditorTab.General}>
              <TailoredSupplyGeneralFields />
            </DashboardPageFormTabPanel>
            <DashboardPageFormTabPanel value={TailoredSupplyEditorTab.Apps}>
              <div className="mx-auto w-full max-w-5xl px-5 py-8">
                <TailoredSupplyAppsTable
                  filter={routeFilters.apps}
                  selectedIds={new Set(selectedApps.keys())}
                  onFilterChange={routeFilters.setApps}
                  onSelectionChange={updateApps}
                />
              </div>
            </DashboardPageFormTabPanel>
            <DashboardPageFormTabPanel value={TailoredSupplyEditorTab.Domains}>
              <div className="mx-auto w-full max-w-5xl px-5 py-8">
                <TailoredSupplyDomainsTable
                  filter={routeFilters.domains}
                  selectedIds={new Set(selectedDomains.keys())}
                  onFilterChange={routeFilters.setDomains}
                  onSelectionChange={updateDomains}
                />
              </div>
            </DashboardPageFormTabPanel>
          </DashboardPageFormTabs>
          {form.formState.errors.root?.server?.message ? (
            <p role="alert" className="text-destructive px-5 pb-3 text-sm">
              {form.formState.errors.root.server.message}
            </p>
          ) : null}
          <DashboardPageFormActions>
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => void returnTo()}
            >
              {dashboardT('tailoredSupply.cancel')}
            </Button>
            <Button type="submit" disabled={!hasSelection || mutation.isPending}>
              {dashboardT(mutation.isPending ? 'tailoredSupply.saving' : 'tailoredSupply.save')}
            </Button>
          </DashboardPageFormActions>
        </form>
      </Form>
    </DashboardPageFormShell>
  );
}

function updateSelection<TItem extends { id: string }>(
  current: Map<string, TItem>,
  items: readonly TItem[],
  selected: boolean,
) {
  const next = new Map(current);
  items.forEach((item) => {
    if (selected) next.set(item.id, item);
    else next.delete(item.id);
  });
  return next;
}
